import type { ReactNode } from 'react';
import { usePrefs, type Prefs } from './prefs';

/**
 * Badge Studio (zoals DexHub en Xperience): welke badges je ziet, in welke volgorde, in welke stijl
 * en waar. Eén lijst met vaste id's; alle labels die tumedia maakt ("Dolby Vision", "DV", "HDR10+",
 * "TrueHD", "5.1"…) worden daarop omgezet. Logo's zijn een eigen, typografische set (geen merklogo's).
 */

export type BadgeKind = 'res' | 'hdr' | 'video' | 'audio' | 'channels';
export interface BadgeDef { id: string; label: string; kind: BadgeKind; hint: string }

export const BADGES: BadgeDef[] = [
  { id: '4k', label: '4K', kind: 'res', hint: 'Ultra HD (2160p)' },
  { id: '1080p', label: '1080p', kind: 'res', hint: 'Full HD' },
  { id: 'remux', label: 'Remux', kind: 'video', hint: 'Origineel van de disc, ongecomprimeerd' },
  { id: 'dv', label: 'Dolby Vision', kind: 'hdr', hint: 'Dynamische HDR' },
  { id: 'hdr10p', label: 'HDR10+', kind: 'hdr', hint: 'Dynamische HDR (Samsung)' },
  { id: 'hdr', label: 'HDR', kind: 'hdr', hint: 'HDR10 of HLG' },
  { id: 'hevc', label: 'HEVC', kind: 'video', hint: 'H.265-codec' },
  { id: 'av1', label: 'AV1', kind: 'video', hint: 'AV1-codec' },
  { id: 'atmos', label: 'Atmos', kind: 'audio', hint: 'Dolby Atmos (3D-geluid)' },
  { id: 'truehd', label: 'TrueHD', kind: 'audio', hint: 'Dolby TrueHD (verliesvrij)' },
  { id: 'dtsx', label: 'DTS:X', kind: 'audio', hint: 'DTS:X (3D-geluid)' },
  { id: 'dtshd', label: 'DTS-HD', kind: 'audio', hint: 'DTS-HD Master Audio' },
  { id: 'ddp', label: 'DD+', kind: 'audio', hint: 'Dolby Digital Plus' },
  { id: '71', label: '7.1', kind: 'channels', hint: '7.1 kanalen' },
  { id: '51', label: '5.1', kind: 'channels', hint: '5.1 kanalen' },
];
const BY_ID = new Map(BADGES.map((b) => [b.id, b]));

/** Elk label dat tumedia maakt → badge-id (of null: geen badge, bijv. "Klaar"). */
export function badgeId(label: string): string | null {
  const l = label.toLowerCase().replace(/\s+/g, ' ').trim();
  if (l === '4k' || l === '2160p' || l === 'uhd') return '4k';
  if (l === '1080p') return '1080p';
  if (l === 'remux') return 'remux';
  if (l.startsWith('dolby vision') || l === 'dv') return 'dv';
  if (l === 'hdr10+') return 'hdr10p';
  if (l === 'hdr' || l === 'hdr10' || l === 'hlg') return 'hdr';
  if (l === 'hevc' || l === 'h.265' || l === 'x265') return 'hevc';
  if (l === 'av1') return 'av1';
  if (l.includes('atmos')) return 'atmos';
  if (l === 'truehd') return 'truehd';
  if (l === 'dts:x' || l === 'dts-x') return 'dtsx';
  if (l.startsWith('dts-hd') || l === 'dts-hd ma' || l === 'dts') return 'dtshd';
  if (l === 'dd+' || l === 'ddp' || l === 'eac3' || l === 'e-ac3') return 'ddp';
  if (l === '7.1') return '71';
  if (l === '5.1') return '51';
  return null;
}

export type BadgeStyle = 'text' | 'color' | 'logo' | 'pack';
export type BadgePlace = 'posters' | 'title' | 'sources' | 'player';
export interface BadgePrefs {
  /** Volgorde (alle id's); wat in hidden staat wordt niet getoond */
  order: string[];
  hidden: string[];
  style: BadgeStyle;
  places: Record<BadgePlace, boolean>;
  /** Geïmporteerd pakket: plaatje per badge-id */
  pack?: { name: string; images: Record<string, string> } | null;
}

export const DEFAULT_BADGES: BadgePrefs = {
  order: BADGES.map((b) => b.id),
  hidden: ['1080p', 'av1'],
  style: 'logo',
  places: { posters: true, title: true, sources: true, player: true },
  pack: null,
};

export function badgePrefsOf(p: Prefs): BadgePrefs {
  const b: Partial<BadgePrefs> = p.badges ?? {};
  const order = [...(b.order ?? []).filter((x) => BY_ID.has(x)), ...DEFAULT_BADGES.order.filter((x) => !(b.order ?? []).includes(x))];
  return {
    order,
    hidden: b.hidden ?? DEFAULT_BADGES.hidden,
    style: b.style ?? DEFAULT_BADGES.style,
    places: { ...DEFAULT_BADGES.places, ...(b.places ?? {}) },
    pack: b.pack ?? null,
  };
}

/**
 * Labels filteren en sorteren volgens de Badge Studio. Labels die geen badge zijn (bijv. "Klaar", "NL")
 * laat hij staan, vooraan. Plaats uit = alleen die labels.
 */
export function applyBadges(labels: string[], bp: BadgePrefs, place: BadgePlace): { other: string[]; badges: { id: string; label: string }[] } {
  const other: string[] = [];
  const seen = new Map<string, string>();
  for (const l of labels) {
    const id = badgeId(l);
    if (!id) other.push(l);
    else if (!seen.has(id)) seen.set(id, l);
  }
  if (!bp.places[place]) return { other, badges: [] };
  const badges = bp.order.filter((id) => seen.has(id) && !bp.hidden.includes(id)).map((id) => ({ id, label: seen.get(id)! }));
  return { other, badges };
}

export function useBadgePrefs(): BadgePrefs {
  return badgePrefsOf(usePrefs());
}

/** Eén badge in de gekozen stijl. */
export function Badge({ id, label, style, pack, size = 'md' }: { id: string; label: string; style: BadgeStyle; pack?: BadgePrefs['pack']; size?: 'sm' | 'md' | 'lg' }) {
  const def = BY_ID.get(id);
  const kind = def?.kind ?? 'video';
  const img = style === 'pack' ? pack?.images[id] : undefined;
  if (img) return <img className={`bdg bdg-img bdg-${size}`} src={img} alt={def?.label ?? label} />;
  if (style === 'logo' || style === 'pack') return <span className={`bdg bdg-logo bdg-${size} bdgk-${kind}`} title={def?.hint} aria-label={def?.label ?? label}>{logoOf(id, label)}</span>;
  return <span className={`bdg bdg-${style === 'color' ? 'color' : 'text'} bdg-${size} bdgk-${kind}`} title={def?.hint}>{shortLabel(id, label)}</span>;
}

function shortLabel(id: string, label: string): string {
  return id === 'dv' ? 'DV' : id === 'hdr10p' ? 'HDR10+' : BY_ID.get(id)?.label ?? label;
}

/** Eigen typografische "logo's" (geïnspireerd op Kodi-skins, zonder merklogo's). */
function logoOf(id: string, label: string): ReactNode {
  switch (id) {
    case '4k': return <><b className="lg-big">4K</b><i className="lg-band">Ultra HD</i></>;
    case '1080p': return <><b className="lg-big">1080</b><i className="lg-band">Full HD</i></>;
    case 'remux': return <><svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="M20 11a8 8 0 0 0-14.3-4.9L4 8M4 13a8 8 0 0 0 14.3 4.9L20 16" /><path d="M4 3v5h5M20 21v-5h-5" /></svg><b className="lg-wide">REMUX</b></>;
    case 'dv': return <span className="lg-stack"><i>Dolby</i><b>VISION</b></span>;
    case 'hdr10p': return <><b className="lg-big">HDR</b><b className="lg-sup">10+</b></>;
    case 'hdr': return <b className="lg-big">HDR</b>;
    case 'hevc': return <b className="lg-hevc">HEVC</b>;
    case 'av1': return <b className="lg-hevc">AV1</b>;
    case 'atmos': return <span className="lg-stack"><i>Dolby</i><b>ATMOS</b></span>;
    case 'truehd': return <><b className="lg-true">TRUE</b><b className="lg-box">HD</b></>;
    case 'dtsx': return <><b className="lg-dts">dts</b><b className="lg-x">:X</b></>;
    case 'dtshd': return <><b className="lg-dts">dts</b><i className="lg-band">HD MA</i></>;
    case 'ddp': return <span className="lg-stack"><i>Dolby</i><b>DIGITAL+</b></span>;
    case '71':
    case '51': return <><svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M4 9h4l5-4v14l-5-4H4z" /><path d="M16 9a4 4 0 0 1 0 6M18.5 6.5a8 8 0 0 1 0 11" /></svg><b className="lg-big">{id === '71' ? '7.1' : '5.1'}</b></>;
    default: return <b>{label}</b>;
  }
}

/** Rij badges + andere labels, voor titelpagina, speler en bronnen. */
export function BadgeRow({ labels, place, size = 'md', className }: { labels: string[]; place: BadgePlace; size?: 'sm' | 'md' | 'lg'; className?: string }) {
  const bp = useBadgePrefs();
  const { badges } = applyBadges(labels, bp, place);
  if (!badges.length) return null;
  return (
    <span className={`bdg-row ${className ?? ''}`} aria-label={badges.map((b) => BY_ID.get(b.id)?.label ?? b.label).join(', ')}>
      {badges.map((b) => <Badge key={b.id} id={b.id} label={b.label} style={bp.style} pack={bp.pack} size={size} />)}
    </span>
  );
}

/**
 * Welke badges een release-naam krijgt (voorbeeld in de studio, zoals Xperience).
 * Zelfde herkenning als de server (pick.ts), plus resolutie, remux en kanalen.
 */
export function badgesFromRelease(name: string): string[] {
  const t = name.replace(/[._]/g, ' ');
  const out: string[] = [];
  if (/\b(2160p|4k|uhd)\b/i.test(t)) out.push('4K');
  else if (/\b1080p\b/i.test(t)) out.push('1080p');
  if (/\bremux\b/i.test(t)) out.push('Remux');
  if (/\b(dv|dovi|dolby ?vision)\b/i.test(t)) out.push('Dolby Vision');
  if (/\bhdr10(\+|plus)\b/i.test(t)) out.push('HDR10+');
  else if (/\bhdr(10)?\b/i.test(t)) out.push('HDR');
  if (/\b(hevc|[hx] ?265)\b/i.test(t)) out.push('HEVC');
  else if (/\bav1\b/i.test(t)) out.push('AV1');
  if (/atmos/i.test(t)) out.push('Atmos');
  if (/truehd/i.test(t)) out.push('TrueHD');
  if (/\bdts[ -:]?x\b/i.test(t)) out.push('DTS:X');
  else if (/\bdts[ -]?hd\b/i.test(t)) out.push('DTS-HD');
  if (/\b(ddp|dd\+|e-?ac-?3)\b/i.test(t)) out.push('DD+');
  if (/\b7[ .]1\b/.test(t)) out.push('7.1');
  else if (/\b5[ .]1\b/.test(t)) out.push('5.1');
  return out;
}
